// Página AdminOrders - Gestión de órdenes de venta
// Listado de pedidos con filtros, búsqueda y cambio de estado

import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { getAllOrders, updateOrderStatus } from '../../services/orders.service'
import { formatCurrency, formatDate } from '../../utils/helpers'
import Button from '../../components/Button'
import ConfirmDialog from '../../components/ConfirmDialog'
import Toast from '../../components/Toast'
import { notifyOrderDelivered } from '../../services/notifications.service'
import './AdminOrders.css'

/**
 * Página AdminOrders
 * Gestión de órdenes de venta para administradores
 */
function AdminOrders() {
  const [orders, setOrders] = useState([])
  const [filteredOrders, setFilteredOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Filtros y búsqueda
  const [statusFilter, setStatusFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('createdAt')
  const [sortDirection, setSortDirection] = useState('desc')

  // Estados para diálogos
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [showStatusDialog, setShowStatusDialog] = useState(false)
  const [newStatus, setNewStatus] = useState('')
  const [notification, setNotification] = useState(null)

  // Estadísticas
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    processing: 0,
    shipped: 0,
    delivered: 0,
    cancelled: 0
  })

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const ordersData = await getAllOrders()
      setOrders(ordersData)
      
      // Calcular estadísticas
      calculateStats(ordersData)
    } catch (err) {
      console.error('Error cargando pedidos:', err)
      setError('Error al cargar los pedidos')
    } finally {
      setLoading(false)
    }
  }, [])

  const calculateStats = (ordersData) => {
    const statsData = {
      total: ordersData.length,
      pending: 0,
      confirmed: 0,
      processing: 0,
      shipped: 0,
      delivered: 0,
      cancelled: 0
    }

    ordersData.forEach(order => {
      const status = order.status || 'pending'
      if (Object.prototype.hasOwnProperty.call(statsData, status)) {
        statsData[status]++
      }
    })

    setStats(statsData)
  }

  const applyFilters = useCallback(() => {
    let filtered = [...orders]

    // Filtrar por estado
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter)
    }

    // Filtrar por búsqueda (número de pedido, nombre del cliente, email)
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(order => {
        const orderNumber = (order.orderNumber || '').toLowerCase()
        const customerName = (order.customer?.name || '').toLowerCase()
        const customerEmail = (order.customer?.email || '').toLowerCase()
        return orderNumber.includes(search) || 
               customerName.includes(search) || 
               customerEmail.includes(search)
      })
    }

    // Ordenar
    filtered.sort((a, b) => {
      let aValue = a[sortBy]
      let bValue = b[sortBy]

      // Manejar Timestamps de Firestore
      if (aValue && aValue.toDate) {
        aValue = aValue.toDate().getTime()
      }
      if (bValue && bValue.toDate) {
        bValue = bValue.toDate().getTime()
      }

      // Manejar strings
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase()
      }
      if (typeof bValue === 'string') {
        bValue = bValue.toLowerCase()
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
      }
    })

    setFilteredOrders(filtered)
  }, [orders, statusFilter, searchTerm, sortBy, sortDirection])

  // Cargar pedidos
  useEffect(() => {
    fetchOrders()
  }, [fetchOrders])

  // Aplicar filtros y ordenamiento
  useEffect(() => {
    applyFilters()
  }, [applyFilters])

  const handleStatusChange = (order, newStatusValue) => {
    setSelectedOrder(order)
    setNewStatus(newStatusValue)
    setShowStatusDialog(true)
  }

  const confirmStatusChange = async () => {
    if (!selectedOrder || !newStatus) return

    try {
      await updateOrderStatus(selectedOrder.id, newStatus)
      if (newStatus === 'delivered') {
        notifyOrderDelivered(selectedOrder.id).catch(() => null)
      }
      
      // Actualizar el pedido en el estado local
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.id === selectedOrder.id
            ? { ...order, status: newStatus }
            : order
        )
      )

      setNotification({
        type: 'success',
        message: `Estado del pedido ${selectedOrder.orderNumber} actualizado correctamente`
      })
      setTimeout(() => setNotification(null), 3000)

      // Recalcular estadísticas
      const updatedOrders = orders.map(order =>
        order.id === selectedOrder.id ? { ...order, status: newStatus } : order
      )
      calculateStats(updatedOrders)
    } catch (err) {
      console.error('Error actualizando estado:', err)
      setNotification({
        type: 'error',
        message: 'Error al actualizar el estado del pedido'
      })
      setTimeout(() => setNotification(null), 3000)
    } finally {
      setShowStatusDialog(false)
      setSelectedOrder(null)
      setNewStatus('')
    }
  }

  const exportToCSV = () => {
    const headers = [
      'Número de Pedido',
      'Fecha',
      'Cliente',
      'Email',
      'Teléfono',
      'Estado',
      'Subtotal',
      'Envío',
      'Total'
    ]

    const rows = filteredOrders.map(order => {
      const date = order.createdAt?.toDate 
        ? formatDate(order.createdAt.toDate())
        : formatDate(new Date())

      return [
        order.orderNumber || '',
        date,
        order.customer?.name || '',
        order.customer?.email || '',
        order.customer?.phone || '',
        getStatusLabel(order.status),
        order.subtotal || 0,
        order.shipping || 0,
        order.total || 0
      ]
    })

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `pedidos_${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    setNotification({
      type: 'success',
      message: `Se exportaron ${filteredOrders.length} pedidos a CSV`
    })
    setTimeout(() => setNotification(null), 3000)
  }

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pendiente',
      confirmed: 'Confirmado',
      processing: 'En Proceso',
      shipped: 'Enviado',
      delivered: 'Entregado',
      cancelled: 'Cancelado'
    }
    return labels[status] || status
  }

  const getStatusClass = (status) => {
    return `status-badge status-${status}`
  }

  if (loading) {
    return (
      <div className="admin-orders-page">
        <div className="loading-state">
          <p>Cargando pedidos...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="admin-orders-page">
        <div className="error-state">
          <p>{error}</p>
          <Button onClick={fetchOrders}>Reintentar</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="admin-orders-page">
      {/* Toast de notificación */}
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      {/* Header */}
      <div className="orders-header">
        <div>
          <h1>Gestión de Pedidos</h1>
          <p className="orders-subtitle">
            Administra y gestiona todos los pedidos de clientes
          </p>
        </div>
        <Button onClick={exportToCSV} variant="outline">
          📥 Exportar CSV
        </Button>
      </div>

      {/* Estadísticas */}
      <div className="orders-stats">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total</div>
        </div>
        <div className="stat-card stat-pending">
          <div className="stat-value">{stats.pending}</div>
          <div className="stat-label">Pendientes</div>
        </div>
        <div className="stat-card stat-confirmed">
          <div className="stat-value">{stats.confirmed}</div>
          <div className="stat-label">Confirmados</div>
        </div>
        <div className="stat-card stat-processing">
          <div className="stat-value">{stats.processing}</div>
          <div className="stat-label">En Proceso</div>
        </div>
        <div className="stat-card stat-shipped">
          <div className="stat-value">{stats.shipped}</div>
          <div className="stat-label">Enviados</div>
        </div>
        <div className="stat-card stat-delivered">
          <div className="stat-value">{stats.delivered}</div>
          <div className="stat-label">Entregados</div>
        </div>
        <div className="stat-card stat-cancelled">
          <div className="stat-value">{stats.cancelled}</div>
          <div className="stat-label">Cancelados</div>
        </div>
      </div>

      {/* Filtros y búsqueda */}
      <div className="orders-filters">
        <div className="filter-group">
          <label htmlFor="statusFilter">Estado:</label>
          <select
            id="statusFilter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Todos</option>
            <option value="pending">Pendiente</option>
            <option value="confirmed">Confirmado</option>
            <option value="processing">En Proceso</option>
            <option value="shipped">Enviado</option>
            <option value="delivered">Entregado</option>
            <option value="cancelled">Cancelado</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="searchTerm">Buscar:</label>
          <input
            type="text"
            id="searchTerm"
            placeholder="Número de pedido, cliente, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label htmlFor="sortBy">Ordenar por:</label>
          <select
            id="sortBy"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="createdAt">Fecha</option>
            <option value="orderNumber">Número de Pedido</option>
            <option value="total">Total</option>
            <option value="status">Estado</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="sortDirection">Dirección:</label>
          <select
            id="sortDirection"
            value={sortDirection}
            onChange={(e) => setSortDirection(e.target.value)}
          >
            <option value="desc">Descendente</option>
            <option value="asc">Ascendente</option>
          </select>
        </div>
      </div>

      {/* Tabla de pedidos - Desktop */}
      <div className="orders-table-container">
        {filteredOrders.length === 0 ? (
          <div className="empty-state">
            <p>No se encontraron pedidos con los filtros aplicados</p>
          </div>
        ) : (
          <table className="orders-table">
            <thead>
              <tr>
                <th>Número</th>
                <th>Fecha</th>
                <th>Cliente</th>
                <th>Email</th>
                <th>Total</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => {
                const date = order.createdAt?.toDate 
                  ? formatDate(order.createdAt.toDate())
                  : formatDate(new Date())

                return (
                  <tr key={order.id}>
                    <td>
                      <Link 
                        to={`/admin/pedidos/${order.id}`}
                        className="order-number-link"
                      >
                        {order.orderNumber || order.id}
                      </Link>
                    </td>
                    <td>{date}</td>
                    <td>{order.customer?.name || 'N/A'}</td>
                    <td>{order.customer?.email || 'N/A'}</td>
                    <td className="total-cell">{formatCurrency(order.total || 0)}</td>
                    <td>
                      <span className={getStatusClass(order.status)}>
                        {getStatusLabel(order.status)}
                      </span>
                    </td>
                    <td>
                      <div className="order-actions">
                        <Link to={`/admin/pedidos/${order.id}`}>
                          <Button variant="outline" size="small">
                            Ver
                          </Button>
                        </Link>
                        {order.status !== 'delivered' && order.status !== 'cancelled' && (
                          <select
                            className="status-select"
                            value={order.status}
                            onChange={(e) => handleStatusChange(order, e.target.value)}
                          >
                            <option value="pending">Pendiente</option>
                            <option value="confirmed">Confirmado</option>
                            <option value="processing">En Proceso</option>
                            <option value="shipped">Enviado</option>
                            <option value="delivered">Entregado</option>
                            <option value="cancelled">Cancelado</option>
                          </select>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Cards de pedidos - Mobile */}
      <div className="orders-cards-container">
        {filteredOrders.length === 0 ? (
          <div className="empty-state">
            <p>No se encontraron pedidos con los filtros aplicados</p>
          </div>
        ) : (
          filteredOrders.map((order) => {
            const date = order.createdAt?.toDate 
              ? formatDate(order.createdAt.toDate())
              : formatDate(new Date())

            return (
              <div key={order.id} className="order-card">
                <div className="order-card-header">
                  <Link 
                    to={`/admin/pedidos/${order.id}`}
                    className="order-card-number"
                  >
                    {order.orderNumber || order.id}
                  </Link>
                  <span className={getStatusClass(order.status)}>
                    {getStatusLabel(order.status)}
                  </span>
                </div>
                <div className="order-card-body">
                  <div className="order-card-info-row">
                    <span className="info-label">Fecha:</span>
                    <span className="info-value">{date}</span>
                  </div>
                  <div className="order-card-info-row">
                    <span className="info-label">Cliente:</span>
                    <span className="info-value">{order.customer?.name || 'N/A'}</span>
                  </div>
                  <div className="order-card-info-row">
                    <span className="info-label">Email:</span>
                    <span className="info-value">{order.customer?.email || 'N/A'}</span>
                  </div>
                  <div className="order-card-info-row">
                    <span className="info-label">Total:</span>
                    <span className="info-value total-value">{formatCurrency(order.total || 0)}</span>
                  </div>
                </div>
                <div className="order-card-actions">
                  <Link to={`/admin/pedidos/${order.id}`}>
                    <Button variant="outline" size="small" style={{ width: '100%' }}>
                      Ver Detalle
                    </Button>
                  </Link>
                  {order.status !== 'delivered' && order.status !== 'cancelled' && (
                    <select
                      className="status-select-mobile"
                      value={order.status}
                      onChange={(e) => handleStatusChange(order, e.target.value)}
                    >
                      <option value="pending">Pendiente</option>
                      <option value="confirmed">Confirmado</option>
                      <option value="processing">En Proceso</option>
                      <option value="shipped">Enviado</option>
                      <option value="delivered">Entregado</option>
                      <option value="cancelled">Cancelado</option>
                    </select>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Diálogo de confirmación de cambio de estado */}
      <ConfirmDialog
        isOpen={showStatusDialog}
        title="Cambiar Estado del Pedido"
        message={`¿Estás seguro de que deseas cambiar el estado del pedido ${selectedOrder?.orderNumber} a "${getStatusLabel(newStatus)}"?`}
        confirmText="Confirmar"
        cancelText="Cancelar"
        onConfirm={confirmStatusChange}
        onClose={() => {
          setShowStatusDialog(false)
          setSelectedOrder(null)
          setNewStatus('')
        }}
      />
    </div>
  )
}

export default AdminOrders

